/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useMemo } from 'react';
import styled from 'styled-components';

import { setSearchParamsForSearchResults } from '@toucan/client/global/nextjs';

import { Link, LinkSize } from '@toucan/client/shared/components/link';

import {
    getActiveFilters,
    getSelectedItemsAfterRemovalOfActiveFilter,
} from '@toucan/client/features/search/components/model-search-results/components/filters-inline/components/filter-results-summary/helpers/active-filters';
import { ActiveFilter } from '@toucan/client/features/search/components/model-search-results/components/filters/types';
import { useSearchParams } from '@toucan/client/features/search/components/model-search-results/hooks/use-search-params';

import { getFiltersToRemove } from '../../../../helpers/filters';
import { transformFiltersToSearchParams } from '../../helpers';
import { ActiveFilterList } from './components/active-filter-list';
import { DEFAULT_TEST_ID } from './constants';
import { styles } from './filter-results-summary.styles';
import {
    FilterResultsSummaryProps,
    StyledFilterResultsSummaryProps,
} from './types';

const StyledFilterResultsSummary = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})<StyledFilterResultsSummaryProps>`
    ${styles}
`;

export const FilterResultsSummary: FunctionComponent<
    FilterResultsSummaryProps
> = ({
    filters,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const router = useRouter();
    const { selectedFilters } = useSearchParams();

    const activeFilters = useMemo(
        () => getActiveFilters(filters, selectedFilters),
        [filters, selectedFilters]
    );

    function handleRemove(activeFilter: ActiveFilter) {
        const selectedItems = getSelectedItemsAfterRemovalOfActiveFilter(
            activeFilter,
            selectedFilters
        );

        const searchParams = transformFiltersToSearchParams(
            activeFilter,
            selectedItems,
            selectedFilters
        );

        setSearchParamsForSearchResults(router, {
            ...searchParams,
            page: undefined,
        });
    }

    const handleClear = () => {
        const filtersToRemove = getFiltersToRemove(activeFilters);

        setSearchParamsForSearchResults(router, {
            ...filtersToRemove,
            page: undefined,
        });
    };

    return activeFilters.length !== 0 ? (
        <StyledFilterResultsSummary
            filters={filters}
            data-testid={dataTestId}
            {...otherProps}
        >
            <ActiveFilterList
                data-testid={`${dataTestId}__filters`}
                className="filter-results-list"
                title={t('Filters')}
                activeFilters={activeFilters}
                onRemove={handleRemove}
            />
            <Link
                size={LinkSize.SMALL}
                className="filter-results-link"
                onClick={handleClear}
                data-testid="filter-results-summary__clear"
            >
                {t('Clear all filters')}
            </Link>
        </StyledFilterResultsSummary>
    ) : null;
};
